import * as React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  IconButton,
  NavItem,
  NavSection,
  Sidebar,
  useToast,
} from '@phinest/ui';
import {
  BadgeDollarSign,
  Banknote,
  CalendarDays,
  Check,
  ChevronsUpDown,
  FileClock,
  FileText,
  Files,
  Gauge,
  Inbox,
  LogOut,
  Menu,
  Plus,
  Receipt,
  Settings,
  Sparkles,
  ClipboardCheck,
  MessageSquareText,
  Users,
  Wrench,
  X,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import {
  useHasTool,
  useMemberships,
  useSelectWorkspace,
  useTerms,
  useWorkspace,
} from '@/lib/workspace';
import { useTool } from '@/lib/query';
import { RouterLink } from './router-link';

interface NavEntry {
  href: string;
  label: string;
  icon: React.ReactNode;
  tool?: string;
  badge?: number;
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const workspace = useWorkspace();
  const terms = useTerms();
  const hasTool = useHasTool();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  React.useEffect(() => setMobileOpen(false), [location.pathname]);

  // Drives the human-in-the-loop badge from every visible source workflow.
  const operatorDesk = useTool<{ counts: { active: number } }>(
    'list_operator_handoffs',
    { scope: 'active', limit: 1 },
    { enabled: hasTool('list_operator_handoffs'), staleTime: 30_000 },
  );
  const pending = operatorDesk.data?.counts.active ?? 0;
  const intake = useTool<{ items: Array<{ status: string; new_submission_count: number }> }>(
    'list_intake_forms',
    {},
    { enabled: hasTool('list_intake_forms'), staleTime: 30_000 },
  );
  const intakeForms = intake.data?.items ?? [];
  const intakeVisible = intakeForms.some(
    (form) => form.status === 'published' || form.new_submission_count > 0,
  );
  const leadAttention = useTool<{ count: number }>(
    'list_lead_response_attention',
    { limit: 1 },
    { enabled: intakeVisible && hasTool('list_lead_response_attention'), staleTime: 30_000 },
  );
  const intakePending =
    leadAttention.data?.count ??
    intakeForms.reduce((count, form) => count + form.new_submission_count, 0);

  const groups: { label?: string; items: NavEntry[] }[] = [
    {
      items: [
        ...(intakeVisible
          ? [
              {
                href: '/requests',
                label: 'Requests',
                icon: <MessageSquareText size={16} />,
                tool: 'list_intake_submissions',
                badge: intakePending,
              },
            ]
          : []),
        {
          href: '/today',
          label: 'Today',
          icon: <ClipboardCheck size={16} />,
          tool: 'get_field_day',
        },
        { href: '/', label: 'Dashboard', icon: <Gauge size={16} /> },
        {
          href: '/customers',
          label: terms.many('customer'),
          icon: <Users size={16} />,
          tool: 'search_customers',
        },
        {
          href: '/quotes',
          label: terms.many('quote'),
          icon: <FileText size={16} />,
          tool: 'list_quotes',
        },
        { href: '/jobs', label: terms.many('job'), icon: <Wrench size={16} />, tool: 'list_jobs' },
        {
          href: '/schedule',
          label: terms.many('appointment'),
          icon: <CalendarDays size={16} />,
          tool: 'list_appointments',
        },
      ],
    },
    {
      label: 'Money',
      items: [
        {
          href: '/invoices',
          label: terms.many('invoice'),
          icon: <Receipt size={16} />,
          tool: 'list_invoices',
        },
        {
          href: '/payments',
          label: terms.many('payment'),
          icon: <BadgeDollarSign size={16} />,
          tool: 'list_payments',
        },
        {
          href: '/bank',
          label: 'Bank',
          icon: <Banknote size={16} />,
          tool: 'list_bank_transactions',
        },
        {
          href: '/bills',
          label: 'Bills',
          icon: <FileClock size={16} />,
          tool: 'list_bills',
        },
        {
          href: '/expenses',
          label: terms.many('expense'),
          icon: <Receipt size={16} />,
          tool: 'list_expenses',
        },
      ],
    },
    {
      label: 'Workspace',
      items: [
        {
          href: '/operator',
          label: 'Operator Desk',
          icon: <Inbox size={16} />,
          tool: 'list_operator_handoffs',
          badge: pending,
        },
        { href: '/reports', label: 'Reports', icon: <Gauge size={16} />, tool: 'run_report' },
        {
          href: '/documents',
          label: terms.many('document'),
          icon: <Files size={16} />,
          tool: 'list_documents',
        },
      ],
    },
  ];

  const isActive = (href: string) =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  const nav = (
    <Sidebar
      header={<WorkspaceSwitcher />}
      footer={
        <div className="space-y-1">
          <NavItem
            href="/settings"
            icon={<Settings size={16} />}
            label="Settings"
            active={isActive('/settings')}
            linkComponent={RouterLink}
          />
          <UserMenu />
        </div>
      }
    >
      {groups.map((group, gi) => {
        const items = group.items.filter((i) => !i.tool || hasTool(i.tool));
        if (!items.length) return null;
        return (
          <NavSection key={group.label ?? gi} label={group.label}>
            {items.map((item) => (
              <NavItem
                key={item.href}
                href={item.href}
                icon={item.icon}
                label={item.label}
                active={isActive(item.href)}
                badge={item.badge ? item.badge : undefined}
                linkComponent={RouterLink}
              />
            ))}
          </NavSection>
        );
      })}
    </Sidebar>
  );

  return (
    <div className="min-h-dvh bg-bg">
      {/* Mobile top bar */}
      <div className="sticky top-0 z-40 flex items-center gap-2 border-b border-separator bg-glass px-3 py-2 backdrop-blur-[20px] lg:hidden">
        <IconButton label="Menu" onClick={() => setMobileOpen((v) => !v)}>
          {mobileOpen ? <X size={18} /> : <Menu size={18} />}
        </IconButton>
        <span className="text-[15px] font-semibold tracking-[-0.01em]">{workspace.name}</span>
      </div>

      <div className="lg:grid lg:grid-cols-[248px_1fr]">
        <div className="hidden h-dvh lg:sticky lg:top-0 lg:block">{nav}</div>
        {mobileOpen && (
          <>
            <button
              aria-label="Close menu"
              className="fixed inset-0 z-40 bg-black/20 lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <div className="fixed inset-y-0 left-0 z-50 w-[264px] lg:hidden">{nav}</div>
          </>
        )}
        <main className="min-w-0">{children}</main>
      </div>
    </div>
  );
}

function WorkspaceSwitcher() {
  const workspace = useWorkspace();
  const memberships = useMemberships();
  const selectWorkspace = useSelectWorkspace();
  const toast = useToast();
  const [busy, setBusy] = React.useState<string | null>(null);
  const others = (memberships.data ?? []).filter((m) => m.tenant_id !== workspace.id);

  async function switchTo(tenantId: string) {
    setBusy(tenantId);
    try {
      await selectWorkspace(tenantId);
    } catch (e) {
      toast.error('Could not switch workspace', e instanceof Error ? e.message : undefined);
    } finally {
      setBusy(null);
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex w-full items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-left transition-colors hover:bg-fill-3">
          <Avatar name={workspace.name} size="sm" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13.5px] font-semibold tracking-[-0.01em] text-text">
              {workspace.name}
            </span>
            <span className="block truncate text-[11.5px] capitalize text-text-3">
              {workspace.role}
            </span>
          </span>
          <ChevronsUpDown size={14} className="shrink-0 text-text-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[248px]">
        <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
        <DropdownMenuItem disabled icon={<Check size={15} />}>
          {workspace.name}
        </DropdownMenuItem>
        {others.map((m) => (
          <DropdownMenuItem
            key={m.tenant_id}
            onSelect={() => void switchTo(m.tenant_id)}
            disabled={busy === m.tenant_id}
          >
            {m.name}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/onboarding">
            <Plus size={15} aria-hidden="true" /> New workspace
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = React.useState(false);
  const email = user?.email ?? 'Signed in';

  async function logOut() {
    setBusy(true);
    try {
      await signOut();
      navigate('/login', { replace: true });
    } catch (error) {
      toast.error('Could not log out', error instanceof Error ? error.message : undefined);
      setBusy(false);
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex w-full items-center gap-2.5 rounded-[var(--radius-sm)] px-2.5 py-1.5 text-left transition-colors hover:bg-fill-3">
          <Avatar name={email} size="sm" />
          <span className="min-w-0 flex-1 truncate text-[13px] text-text-2">{email}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[220px]">
        <DropdownMenuLabel>{email}</DropdownMenuLabel>
        <DropdownMenuItem asChild>
          <Link to="/settings/connect">
            <Sparkles size={15} aria-hidden="true" /> Connect an AI client
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          icon={<LogOut size={15} />}
          disabled={busy}
          onSelect={() => void logOut()}
        >
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Page-level "new" button that only renders when the tool is available. */
export function ToolAction({ tool, children }: { tool: string; children: React.ReactNode }) {
  const hasTool = useHasTool();
  if (!hasTool(tool)) return null;
  return <>{children}</>;
}
